import { Box, Chip, Link, Stack } from '@mui/material';
import { DataGridProProps } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import { ReactElement } from 'react';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { PaginatedTableController, PaginatedTableState, PrefilledDataConfig } from '../../../common/controllers/PaginatedTableController';
import { JavaProvider } from '../../../common/reports/models/JavaProvider';
import { DataGridStyleOverrides } from '../../../common/style/data-grid-styles';
import { DefaultHeaderConfiguration, FieldConfiguration } from '../../../common/types/paginated-table';

type State = PaginatedTableState & {};

type Props = {
  javaProviders: JavaProvider[];
  parentLoading: boolean;
};

export class JavaProvidersTable extends PaginatedTableController<JavaProvider, Props, State> {
  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    // This table is completely local and not paginated, and needs to adapt to the
    // update strategy of its container (skeleton on first load, loader on report change).
    // When the input rows change, manually refresh the content of the table
    super.componentDidUpdate(prevProps, prevState);

    if (prevProps.javaProviders !== this.props.javaProviders) {
      this.reloadTableContent();
    }
  }

  protected isLoading() {
    return super.isLoading() || this.props.parentLoading;
  }

  protected getPrefilledConfig(): PrefilledDataConfig<JavaProvider> {
    const { javaProviders, parentLoading } = this.props;
    return {
      prefillActive: true,
      initialLoading: parentLoading,
      initialData: {
        data: javaProviders,
        totalCount: javaProviders.length,
        pageInfo: { hasPreviousPage: false, hasNextPage: false, startCursor: null, endCursor: null },
      },
    };
  }

  protected getDefaultPageSize() {
    // Only used to render the skeleton, this table is not paginated
    return 5;
  }

  protected getAdditionalDataGridProps(): Partial<DataGridProProps<JavaProvider>> {
    return {
      ...super.getAdditionalDataGridProps(),
      classes: {
        row: DataGridStyleOverrides.ROW_AUTO_HEIGHT,
        virtualScrollerContent: DataGridStyleOverrides.DYNAMIC_ROW_HEIGHT,
        virtualScrollerRenderZone: DataGridStyleOverrides.DYNAMIC_ROW_HEIGHT,
      },
      hideFooter: true,
    };
  }

  protected getDefaultHeaderConfigurations(): DefaultHeaderConfiguration {
    return { title: t('cryptoLibrarySecurityTitle') };
  }

  protected getFieldConfigurations(): FieldConfiguration<JavaProvider>[] {
    return [
      {
        fieldKey: 'name',
        label: t('name'),
        description: t('name'),
        columnProps: {
          maxWidth: 400,
        },
        render: (row) => row.getName(),
      },
      {
        fieldKey: 'version',
        label: t('version'),
        description: t('version'),
        columnProps: {
          maxWidth: 400,
        },
        render: (row) => row.getVersion(),
      },
      {
        fieldKey: 'vulnerabilities',
        label: t('issues'),
        description: t('issues'),
        render: (row, { tabIndex }) => {
          if (row.getVulnerabilities().length === 0) {
            return <BlankCell />;
          }
          return (
            <Stack direction="row" flexWrap="wrap" sx={{ py: 1 }}>
              {row.getVulnerabilities().map((v, index) => (
                <Chip
                  key={index}
                  label={v.getName()}
                  component={Link}
                  tabIndex={tabIndex}
                  href={v.getUrl() ?? undefined}
                  target="_blank"
                  clickable
                  sx={{ my: 0.5, mr: 1 }}
                  underline="none"
                  aria-label={t('a11y.general.knowledgeBase', { topic: v.getName() }) as string}
                />
              ))}
            </Stack>
          );
        },
      },
    ];
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [];
  }

  protected renderTable(tableComponent: ReactElement) {
    return <Box data-role="report-java-providers">{tableComponent}</Box>;
  }

  protected async fetchData() {
    const { javaProviders } = this.props;
    return {
      data: javaProviders,
      pageInfo: { hasNextPage: false, hasPreviousPage: false, startCursor: null, endCursor: null },
      totalCount: javaProviders.length,
    };
  }
}
